import CresAxios from "../../util/CresAxios";

import {ChartKind, ChartSpec, TileDrawing} from "./chartSpec";
import {ResultColumn} from "./reportApi";

// RE-EXPORTED so callers that already import from here keep working. It LIVES in chartSpec because
// it is pure chart logic and this module drags axios in, which Jest cannot parse -- a pure function
// is untestable from here.
//
// BELOW EVERY import, not among them: eslint's import/first counts `export ... from` as an import,
// and CRA promotes that to a BUILD FAILURE while tsc is perfectly happy. The same shape as a const
// between imports.
export {chartForTile} from "./chartSpec";

/**
 * Dashboards: a named, ordered set of tiles, each a saved report and how to draw it.
 *
 * A DASHBOARD HOLDS NO ROWS. Reading one returns the arrangement; asking what its reports say is a
 * separate call, because an editor needs the first and never the second.
 *
 * ONE CALL FOR THE WHOLE PAGE. `runDashboard` returns every tile's result together rather than the
 * page firing one request per tile. NOT for capacity -- the prod Lambda concurrency cap is 1000,
 * checked in Service Quotas, and an earlier version of this comment said 10 and was wrong. Eight
 * requests is one
 * dashboard consuming the tier, and the failure that produces — some tiles drawing, others
 * throttled — reads as a data problem rather than a capacity one.
 *
 * THE PERSONA IS NOT IN ANY OF THESE TYPES, deliberately. It travels in the request headers like
 * everywhere else in this app, and is verified at the server's boundary. A dashboard that could
 * carry one would be a dashboard that showed its author's rows to its readers.
 */

const URL = "t10/v1/dashboards";

/** A tile as stored: which report, drawn how, how wide. */
export type Tile = {
    USID?: string;
    ReportUSID: string;
    /** The report's own name, sent beside the reference so a tile has a heading before it loads. */
    ReportName?: string | null;
    /** Null means "use the report's name", which is what almost every tile does. */
    Title?: string | null;
    /** "table" or "chart" -- ALL this field decides. Typed loosely because rows saved before the
     *  collapse still hold a chart name, which `drawingOf` reads and `chartForTile` honours as that
     *  tile's fallback chart. Which chart is drawn lives in the specification. */
    ChartKind: TileDrawing | ChartKind;
    /** 1 = half the page, 2 = full. */
    Width: 1 | 2;
    /** True when the report behind this tile has gone. Shown, never hidden. */
    Missing?: boolean;
    /** THE REPORT'S OWN CHART, sent with the tile, so one built on the report page draws the same
     *  way here. Absent until somebody builds one. */
    ChartSpecification?: ChartSpec | null;
    /** THIS TILE'S OWN, when it has been given one. Null means "however the report is drawn",
     *  which is the common case. Both are sent rather than resolved on the server so the page can
     *  SAY which it is showing -- a tile quietly not following its report is the confusing part. */
    TileChartSpecification?: ChartSpec | null;
};


export type Dashboard = {
    USID: string;
    Name: string;
    Description?: string | null;
    /** True on at most one dashboard per account; it is what the Home page shows. */
    IsHome?: boolean;
    /** True = only its author may list, open, run or change it. Cannot also be the home page. */
    IsPrivate?: boolean;
    /** MY OWN CHOICE of Home page, which is not `IsHome`: that is the account's, one per realm,
     *  and this is one per person. A dashboard can be either, both or neither. Sent only when one
     *  dashboard is read -- the list has no use for it. */
    MyHome?: boolean;
    /** Whether the signed-in user is that author. */
    Mine?: boolean;
    Tiles: Tile[];
};

export type DashboardSummary = {
    USID: string;
    Name: string;
    Description?: string | null;
    TileCount: number;
    IsHome?: boolean;
    /** Private ones are listed only to their author, so a listed private one is always yours. */
    IsPrivate?: boolean;
    Mine?: boolean;
    LastModifiedDatetime?: string | null;
};

/** A tile with what its report said — or why it could not say anything. */
export type TileResult = Tile & {
    Columns?: ResultColumn[];
    Rows?: unknown[][];
    RowCount?: number;
    Truncated?: boolean;
    /** Present INSTEAD of rows. One tile's refusal never blanks the page. */
    Refusal?: string;
};

export function getDashboards(): Promise<DashboardSummary[]> {
    return CresAxios({method: "GET", url: URL})
        .then((response: any) => response.data?.Dashboards ?? []);
}

export function getDashboard(usid: string): Promise<Dashboard> {
    return CresAxios({method: "GET", url: URL, params: {USID: usid}})
        .then((response: any) => response.data?.Dashboard);
}

/**
 * The dashboard marked as the account's Home page, or null.
 *
 * ASKED FOR BY NAME, because the caller does not know which one it is -- that is the point of the
 * flag. NULL IS A NORMAL ANSWER: most accounts have not chosen one, and the Home page asks on
 * every visit, so "there is none" must not arrive as an error.
 */
export function getHomeDashboard(): Promise<Dashboard | null> {
    return CresAxios({method: "GET", url: URL, params: {Home: true}})
        .then((response: any) => response.data?.Dashboard ?? null);
}

/**
 * Choose which dashboard MY Home page shows, or clear the choice by passing null.
 *
 * A PREFERENCE ABOUT A PERSON, kept per account by the server rather than in this browser --
 * sessionStorage dies with the tab and localStorage is per machine, and the same person on a
 * laptop and a desktop would get two different Home pages with nothing to explain why.
 *
 * NULL CLEARS IT, which is how somebody goes back to whatever the account chose rather than being
 * stuck with a page they picked once.
 */
export function setHomeDashboard(usid: string | null) {
    return CresAxios({method: "PUT", url: URL, data: {HomeDashboardUSID: usid}})
        .then((response: any) => response.data);
}

export function saveDashboard(dashboard: {USID?: string; Name: string; Description?: string | null;
                                          Tiles: Tile[]; IsHome?: boolean;
                                          IsPrivate?: boolean}) {
    return CresAxios({method: "PUT", url: URL, data: dashboard})
        .then((response: any) => response.data);
}

export function deleteDashboard(usid: string) {
    return CresAxios({method: "DELETE", url: URL, params: {USID: usid}})
        .then((response: any) => response.data);
}

/** Run every tile, in one call, under the caller's current persona. */
export function runDashboard(usid: string): Promise<TileResult[]> {
    return CresAxios({method: "PUT", url: URL, data: {USID: usid, Run: true}})
        .then((response: any) => response.data?.Tiles ?? []);
}

/**
 * Give one tile its own chart, or take it away.
 *
 * NULL IS AN INSTRUCTION HERE, unlike saveReport where an absent chart means "leave it alone".
 * This call exists only to set this one field, so there is nothing else a caller could have meant --
 * and clearing it is how a tile goes back to following its report.
 *
 * ONE TILE, NOT THE DASHBOARD. A page that is only SHOWING tiles must not have to resend the
 * arrangement to change how one of them is drawn: a stale tile list is how somebody else's reorder
 * gets quietly undone.
 */
export function setTileChart(tileUSID: string, chart: ChartSpec | null) {
    return CresAxios({method: "PUT", url: URL,
                      data: {TileUSID: tileUSID, ChartSpecification: chart}})
        .then((response: any) => response.data);
}
